import type { ReactNode } from 'react';
import { Alert, Button, Loader } from '@mantine/core';
import { formatDay, formatMoney, type SnapshotRequest } from '@parts/shared';
import { Avatar, Progress, StatusPill } from '../components/ui';
import { useI18n } from '../i18n';
import classes from './purchase/PurchaseScreen.module.css';

/** Строка сведений о выбранном запросе: статус, дата, автор, объём, прогресс закупки. */
export function RequestFacts({ req }: { req: SnapshotRequest }) {
  const { t, tx, tn } = useI18n();
  const pct = Math.round(req.done * 100);
  return (
    <>
      <StatusPill status={req.status} />
      <span className={classes.fact}>{t('от {date}', { date: formatDay(req.created) })}</span>
      <span className={classes.fact}>
        <Avatar name={tx(req.createdBy) || '?'} size={20} />
        {tx(req.createdBy)}
      </span>
      <span className={classes.sep} />
      <span className={classes.fact}>
        <span className={classes.factStrong}>{tn(req.positions, ['позиция', 'позиции', 'позиций'])}</span>
      </span>
      <span className={classes.fact}>
        {t('на')} <span className={classes.factStrong}>{formatMoney(req.amount)}</span>
      </span>
      <span className={classes.sep} />
      <span className={classes.fact}>
        <Progress value={req.done} tone={pct >= 100 ? 'green' : 'accent'} width={80} />
        {t('закуплено')} <span className={classes.factStrong}>{pct}%</span>
      </span>
      {req.deadline && <span className={classes.fact}>{t('срок {date}', { date: formatDay(req.deadline) })}</span>}
      {req.comment && <span className={`${classes.fact} ${classes.infoMuted}`}>{req.comment}</span>}
    </>
  );
}

/** Экран ждёт данные или не смог их загрузить. */
export function EntryState({ error, onRetry, children }: { error: string | null; onRetry: () => void; children?: ReactNode }) {
  const { t, tx } = useI18n();
  if (error)
    return (
      <div className={classes.body}>
        <Alert color="red" variant="light" title={t('Не удалось загрузить данные')} maw={640}>
          {tx(error)}
          <div style={{ marginTop: 12 }}>
            <Button variant="default" onClick={onRetry}>
              {t('Повторить')}
            </Button>
          </div>
        </Alert>
      </div>
    );
  return (
    <div className={classes.loading}>
      <Loader size="sm" />
      {children ?? t('Загружаю данные…')}
    </div>
  );
}
